import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { FilterType } from '@bestseller-bit/frontend-community.bootstrapping.application-shell';
import {
    CircularProgress,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import { FC, useMemo } from 'react';
import { usePartners, useType } from 'src/hooks';
import { useRedirectToOverview } from 'src/hooks/useRedirectToOverview';
import { selectedPartnerVar } from 'src/infrastructure/local_state';

export const PartnerSelection: FC<{
    addFilter: (addition: Array<Omit<FilterType, 'name'>> | Omit<FilterType, 'name'>) => void;
    removeFilter?: (value: string | string[], filterGroup?: string) => void;
}> = ({ addFilter, removeFilter }) => {
    const { redirectToOverview } = useRedirectToOverview();
    const type = useType();
    const { partners, loading } = usePartners(type);
    const [selectedPartner, setSelectedPartner] = useReactiveVar(selectedPartnerVar);

    const sameCountry = useMemo(
        () => partners?.every((partner) => partner.country === partners[0]?.country),
        [partners]
    );

    const sortedPartners = useMemo(() => {
        return partners?.toSorted((partnerA, partnerB) => partnerA.name?.localeCompare(partnerB.name ?? '') ?? -1);
    }, [partners]);

    const handlePartnerChange = (event: SelectChangeEvent) => {
        removeFilter?.('partner', 'partner');
        const { target } = event;
        const newPartner = partners?.find((partner) => partner.compositeId === Number(target.value));

        if (newPartner) {
            addFilter({
                key: 'partner',
                value: newPartner.compositeId?.toString() ?? '',
                displayName: newPartner.name ?? '',
                filterGroup: 'partner',
            });
            setSelectedPartner(newPartner);
            redirectToOverview?.();
        }
    };

    return (
        <FormControl fullWidth>
            <InputLabel id="selected-partner-select-label">Selected partner</InputLabel>
            <Select
                labelId="selected-partner-select-label"
                label="Selected partner"
                value={selectedPartner?.compositeId?.toString() ?? ''}
                defaultValue={''}
                onChange={handlePartnerChange}
                MenuProps={{ marginThreshold: 0, disablePortal: true }}
                endAdornment={
                    loading ? (
                        <InputAdornment position="end" sx={{ pointerEvents: 'none', position: 'absolute', right: 35 }}>
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null
                }
            >
                {loading ? (
                    <MenuItem disabled>
                        <CircularProgress size={20} sx={{ marginRight: 1 }} />
                        Loading...
                    </MenuItem>
                ) : (
                    sortedPartners?.map((partner) => (
                        <MenuItem key={partner.compositeId} value={partner.compositeId?.toString()}>
                            {partner.name} {!sameCountry && `(${partner.country})`}
                        </MenuItem>
                    ))
                )}
            </Select>
        </FormControl>
    );
};
